import { DotLegalDeleteDialog, DotLegalMultiSelect } from "@dotlegal/dotlegal-ui-components";
import React from "react";
import { useTranslation } from "../../../localization/useTranslation.ts";
import { useSelectableBusinessAreaByUserAccessQuery } from "../../../common/hooks/useSelectableItemQueries.ts";
import { ContractModel } from "../Contract.types.ts";
import { useContractResponsibilityHelper } from "../ContractResponsibilityHelper.hooks.ts";
import { Trans } from "react-i18next";

export interface ContractAccessBusinessAreaFieldProps {
    /** Lets a caller that owns the vertical rhythm itself turn the field's own margin off. */
    noMargin?: boolean;
    contract: ContractModel | undefined;
    isLoading: boolean;
    onChange: (changeFunc: (contract: ContractModel) => void) => Promise<void>;
    readOnly: boolean;
    disabled: boolean;
    isPreview: boolean;
    errorText?: string;
    disableAccessWarning?: boolean;
    showWarningAsInfo?: boolean;
    /** Suppresses the field's help icon, for a view where the same field is shown twice. */
    hideToolTip?: boolean;
}

function ContractAccessBusinessAreaField(props: ContractAccessBusinessAreaFieldProps) {
    const { translateString } = useTranslation();
    let businessAreaQuery = useSelectableBusinessAreaByUserAccessQuery(!props.isPreview);

    const { onFieldChange, handleDialogConfirm, pendingUserChanges, setPendingUserChanges } = useContractResponsibilityHelper(
        props.onChange,
        props.disableAccessWarning
    );

    return (
        <>
            <DotLegalMultiSelect
                noMargin={props.noMargin}
                options={businessAreaQuery.data}
                isLoading={(businessAreaQuery.isLoading || props.isLoading) && !props.readOnly}
                chosenOptions={props.contract?.contractAccessBusinessAreas ?? []}
                onChange={
                    props.readOnly
                        ? () => {}
                        : (businessAreas) => {
                              const visibleBusinessAreaIds = businessAreaQuery.data?.map((ba) => ba.id) ?? [];
                              const hiddenBusinessAreas =
                                  props.contract?.contractAccessBusinessAreas.filter((id) => !visibleBusinessAreaIds.includes(id)) ?? [];
                              const allBusinessAreas = [...hiddenBusinessAreas, ...businessAreas];

                              let newContract = { ...props.contract!, contractAccessBusinessAreas: allBusinessAreas };
                              onFieldChange(newContract, props.contract!);
                          }
                }
                label={translateString("businessAreaAccess")}
                noOptionsLabel={translateString("noOptions")}
                disabled={props.disabled}
                toolTipText={
                    props.hideToolTip
                        ? undefined
                        : props.showWarningAsInfo
                          ? `${translateString("contractBusinessAreaAccessTooltip")}\n\n${translateString("contractBusinessAreaAccessWarning")}`
                          : translateString("contractBusinessAreaAccessTooltip")
                }
                warningText={props.isPreview || props.showWarningAsInfo ? undefined : translateString("contractBusinessAreaAccessWarning")}
                errorText={props.errorText}
                disableClearable
            />

            {pendingUserChanges && (
                <DotLegalDeleteDialog
                    open
                    headerText={translateString("confirmAccessRemoval")}
                    alertBodyText={
                        <Trans
                            i18nKey="confirmContractAccessRemovalBody"
                            components={{
                                ul: <ul />,
                                li: <li />,
                            }}
                        />
                    }
                    warningTranslation={translateString("warning")}
                    deleteBtnText={translateString("save")}
                    cancelBtnText={translateString("cancel")}
                    onDialogClose={() => setPendingUserChanges(null)}
                    onOkClick={() => handleDialogConfirm()}
                />
            )}
        </>
    );
}

export default ContractAccessBusinessAreaField;
